import { confirmSignIn, signIn } from "aws-amplify/auth";
import { Dumbbell } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Anmeldung samt erstmaliger Passwortvergabe.
 *
 * Cognito verlangt bei einem neu angelegten Nutzer ein neues Passwort
 * (`CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED`). Ohne diesen Schritt lässt
 * sich ein per Einladung erstellter Zugang gar nicht in Betrieb nehmen — es
 * gibt keine gehostete Anmeldeseite.
 */
export function Anmeldung({ onFertig }: { onFertig: () => void }) {
  const [benutzer, setBenutzer] = useState("");
  const [passwort, setPasswort] = useState("");
  const [neuesPasswort, setNeuesPasswort] = useState("");
  const [brauchtNeuesPasswort, setBrauchtNeuesPasswort] = useState(false);
  const [fehler, setFehler] = useState<string>();
  const [laeuft, setLaeuft] = useState(false);

  async function absenden(event: FormEvent) {
    event.preventDefault();
    setFehler(undefined);
    setLaeuft(true);
    try {
      if (brauchtNeuesPasswort) {
        const { isSignedIn } = await confirmSignIn({
          challengeResponse: neuesPasswort,
        });
        if (isSignedIn) onFertig();
        return;
      }

      const { isSignedIn, nextStep } = await signIn({
        username: benutzer,
        password: passwort,
      });
      if (isSignedIn) {
        onFertig();
      } else if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        setBrauchtNeuesPasswort(true);
      } else {
        setFehler(`Unerwarteter Anmeldeschritt: ${nextStep.signInStep}`);
      }
    } catch (error) {
      setFehler(error instanceof Error ? error.message : String(error));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Dumbbell className="size-5 text-primary" />
            GymBook
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={absenden} className="grid gap-4">
            {brauchtNeuesPasswort ? (
              <>
                <Alert>
                  <AlertDescription>
                    Beim ersten Anmelden muss ein eigenes Passwort vergeben werden.
                  </AlertDescription>
                </Alert>
                <div className="grid gap-2">
                  <Label htmlFor="neues-passwort">Neues Passwort</Label>
                  <Input
                    id="neues-passwort"
                    type="password"
                    value={neuesPasswort}
                    onChange={(e) => setNeuesPasswort(e.target.value)}
                    autoComplete="new-password"
                    autoFocus
                    required
                  />
                  <p className="text-muted-foreground text-sm">Mindestens 8 Zeichen.</p>
                </div>
              </>
            ) : (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="benutzer">Benutzername</Label>
                  <Input
                    id="benutzer"
                    value={benutzer}
                    onChange={(e) => setBenutzer(e.target.value)}
                    autoComplete="username"
                    autoFocus
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="passwort">Passwort</Label>
                  <Input
                    id="passwort"
                    type="password"
                    value={passwort}
                    onChange={(e) => setPasswort(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </div>
              </>
            )}

            {fehler && (
              <Alert variant="destructive">
                <AlertDescription>{fehler}</AlertDescription>
              </Alert>
            )}

            <Button type="submit" size="lg" disabled={laeuft}>
              {brauchtNeuesPasswort ? "Passwort setzen" : "Anmelden"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
